/**
 * StageComponent.module.scss — the page verb's three pieces (board `PageVerb.dc.html`, and the
 * `.fieldRow` / `.pillsRow` / `.verb` block of `ToilePageToWall.dc.html`, which transposed them onto
 * the converged page's own anatomy). The boards' literal hex values are the `--tl-*` tokens
 * (`products/toile/styles/index.scss:65-78`), so they are named rather than repeated: #1C1916 =
 * `--tl-surface`, #3A342C = `--tl-border-strong`, #6C6559 = `--tl-dim`, #A69F91 = `--tl-muted`,
 * #F2EDE4 = `--tl-text`, #FFDFA8 = `--tl-accent`, #26200F = `--tl-accent-ink`.
 *
 * Since plan 2026_09_23_pack_stage_coda.md it sits inside the stage's coda at every width (board
 * `TenthFrame.dc.html` frames B/C): the chips wrap there, centred, never clipped, and the verb waits
 * in outline rather than fading.
 */

.stage {
    margin: 0;
}

.fieldRow {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--tl-surface);
    border: 1px solid var(--tl-border-strong);
    border-radius: 12px;
    padding: 11px 14px;

    &:focus-within {
        border-color: rgba(255, 223, 168, 0.55);
    }
}

.field {
    flex: 1;
    min-width: 0;
    background: none;
    border: none;
    outline: none;
    padding: 0;
    color: var(--tl-text);
    font-family: var(--tl-font);
    font-size: 14px;
    line-height: 1.4;

    &::placeholder {
        color: var(--tl-dim);
    }
}

.chips {
    margin-top: 10px;
    display: flex;
    gap: 8px 7px;
    flex-wrap: wrap;
    justify-content: center;
}

.chip {
    font-family: var(--tl-font);
    font-size: 12px;
    line-height: 1.3;
    color: var(--tl-muted);
    background: none;
    border: 1px solid var(--tl-border-strong);
    border-radius: 9999px;
    padding: 6px 12px;
    max-width: 100%;
    overflow-wrap: anywhere;
    cursor: pointer;
    position: relative;
    transition: color 120ms ease, border-color 120ms ease, background 120ms ease;

    &:hover,
    &:focus-visible {
        color: var(--tl-accent);
        border-color: rgba(255, 223, 168, 0.55);
        background: rgba(255, 223, 168, 0.1);
    }

    // A chip is disabled only while a write is in flight (`StageComponent.tsx`'s own `disabled`
    // comment) — the field reads as settled until that write resolves.
    &:disabled {
        opacity: 0.45;
        cursor: default;
    }

    // Phone tap target: the pill keeps its size (a border wraps padding, so padding is visible);
    // a transparent pseudo-element extends the hit box 4px above and below. The chips wrap now,
    // so the extension stays inside half the 8px row gap and never covers the next row's pill.
    @media (max-width: 899px) {
        &::before {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            top: -4px;
            bottom: -4px;
        }
    }
}

/* The terms gate's card (plan 2026_09_01_tos_gate.md §8.6 step 3) — between the chips and the
   verb, in flow. `TosConsentCard`'s own `.card` (products/toile/wall/TosConsentCard.module.scss)
   carries the border/background; this only places it in the stack. */
.consent {
    margin-top: 10px;
}

.verb {
    margin-top: 12px;
    display: block;
    width: 100%;
    text-align: center;
    font-family: var(--tl-font);
    font-weight: 500;
    font-size: 14px;
    line-height: 1.3;
    color: var(--tl-accent-ink);
    background: var(--tl-accent);
    border: none;
    border-radius: 10px;
    padding: 12px;
    cursor: pointer;
    transition: opacity 120ms ease;
}

/* Waiting is the board's `.verb.waiting` (TenthFrame frame B): the verb at rest is an accent
   outline with accent words — waiting, not broken. `disabled` carries the state to assistive
   technology. */
.verbWaiting {
    color: var(--tl-accent);
    background: transparent;
    border: 1px solid rgba(255, 223, 168, 0.55);
    padding: 11px;
    cursor: default;
}

/* A write in flight: filled, unfaded, only the cursor says so. */
.verbWorking {
    cursor: progress;
}

/* The one line the verb can say for itself — a refusal from `/continue/pack`, in the wall's own
   vocabulary (`chains.ts`'s `errorLineFor`): short, lowercase, no punctuation. Colour and case are
   the Notice component's job now (`@core/ui/Notice`) — this class carries position/type only. */
.note {
    margin: 8px 0 0;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10.5px;
    letter-spacing: 0.12em;
}
